import { useEffect, useRef, useState, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

function isDismissedRecently(key: string): boolean {
  const ts = localStorage.getItem(key);
  if (!ts) return false;
  return Date.now() - Number(ts) < SEVEN_DAYS;
}

export function usePWAInstall() {
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const [canPrompt, setCanPrompt] = useState(false);

  const isInstalled =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true ||
    localStorage.getItem("circle_pwa_installed") === "true";

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const isDesktop = !isIOS && !isAndroid;

  const bannerDismissed = isDismissedRecently("circle_pwa_banner_dismissed");
  const iosGuideDismissed = isDismissedRecently("circle_ios_guide_dismissed");

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      setCanPrompt(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem("circle_pwa_installed", "true");
    }
    deferredPrompt.current = null;
    setCanPrompt(false);
  }, []);

  const dismissBanner = useCallback(() => {
    localStorage.setItem("circle_pwa_banner_dismissed", String(Date.now()));
  }, []);

  const dismissIOSGuide = useCallback(() => {
    localStorage.setItem("circle_ios_guide_dismissed", String(Date.now()));
  }, []);

  return {
    isInstalled,
    isIOS,
    isAndroid,
    isDesktop,
    canPrompt,
    promptInstall,
    bannerDismissed,
    iosGuideDismissed,
    dismissBanner,
    dismissIOSGuide,
  };
}
